import React, { useState, useRef, useEffect } from 'react';
import './UploadProduct.css';
import { storage, auth } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';

const UploadProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { product: existingProduct } = location.state || {};
  const isEditMode = !!existingProduct;

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [warning, setWarning] = useState('');
  const fileInputRef = useRef(null);

  const SIZE_OPTIONS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'ONE SIZE'];
  const LOCATION_OPTIONS = ['MILAN', 'FLORENCE', 'ROME', 'PARIS', 'MADRID', 'AMSTERDAM', 'BARCELONA', 'LONDON', 'OTHER…'];
  const AVAILABILITY_OPTIONS = ['Available anytime', 'Weekdays only', 'Weekends only', 'By request', 'Limited availability'];
  const COLOR_OPTIONS = [
    'Black',
    'White',
    'Ivory / Cream',
    'Grey',
    'Brown',
    'Beige / Nude',
    'Red',
    'Burgundy',
    'Pink',
    'Orange',
    'Yellow',
    'Green',
    'Blue',
    'Navy',
    'Purple',
    'Metallic',
    'Multicolor',
  ];

  const [size, setSize] = useState('');
  const [locationValue, setLocationValue] = useState('');
  const [availabilityValue, setAvailabilityValue] = useState('');
  const [colorValue, setColorValue] = useState('');

  const [productData, setProductData] = useState({
    productName: '',
    type: 'Jackets',
    condition: 'Excellent',
    description: '',
  });

  const isFormValid = () => {
    const requiredFields = ['productName', 'type', 'condition', 'description'];
    const requiredStringFieldsOk = requiredFields.every(
      (field) => productData[field] && productData[field].trim() !== ''
    );

    const requiredSelectsOk =
      size.trim() !== '' &&
      locationValue.trim() !== '' &&
      colorValue.trim() !== '' &&
      availabilityValue.trim() !== '';

    return requiredStringFieldsOk && requiredSelectsOk && (isEditMode || images.length > 0);
  };

  useEffect(() => {
    if (isEditMode && existingProduct) {
      const getMetafieldValue = (key) => {
        const metafield = existingProduct.metafields.edges.find((edge) => edge.node.key === key);
        return metafield ? metafield.node.value : '';
      };

      const firstCsvValue = (value) => {
        if (!value || typeof value !== 'string') return '';
        const first = value.split(',')[0];
        return (first || '').trim();
      };

      setProductData({
        productName: existingProduct.title,
        type: getMetafieldValue('type'),
        condition: getMetafieldValue('condition'),
        description: getMetafieldValue('description'),
      });

      setSize(firstCsvValue(getMetafieldValue('size')));
      setLocationValue(firstCsvValue(getMetafieldValue('location')));
      setColorValue(firstCsvValue(getMetafieldValue('colors')));
      setAvailabilityValue(firstCsvValue(getMetafieldValue('availability')));

      if (existingProduct.images && existingProduct.images.edges.length > 0) {
        const imageUrls = existingProduct.images.edges.map((edge) => edge.node.originalSrc);
        setImagePreviews(imageUrls);
      }
    }
  }, [isEditMode, existingProduct]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = [...images, ...files];
    setImages(newImages);

    const newImagePreviews = newImages.map(file => URL.createObjectURL(file));
    setImagePreviews(newImagePreviews);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    const newImagePreviews = imagePreviews.filter((_, i) => i !== index);
    setImages(newImages);
    setImagePreviews(newImagePreviews);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    // if (id === 'price') {
    //   const sanitizedValue = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    //   setProductData((prevData) => ({
    //     ...prevData,
    //     [id]: sanitizedValue,
    //   }));
    // } else {
      setProductData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    // }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      setWarning('Please fill out all the fields before submitting');
      return;
    }
    setWarning('');
    setIsLoading(true);

    try {
      // In edit mode, keep existing URLs unless user removes them; add newly uploaded files on top.
      let imageUrls = isEditMode ? [...imagePreviews] : [];
      if (images.length > 0) {
        const uploadedUrls = await Promise.all(
          images.map(async (image) => {
            const storageRef = ref(storage, `products/${Date.now()}_${image.name}`);
            await uploadBytes(storageRef, image);
            return await getDownloadURL(storageRef);
          })
        );
        imageUrls = isEditMode ? [...imagePreviews, ...uploadedUrls] : uploadedUrls;
      }

      const currentUser = auth.currentUser;
      const formData = {
        ...productData,
        size,
        location: locationValue,
        color: colorValue,
        availability: availabilityValue,
        images: imageUrls,
        vendorId: currentUser ? currentUser.uid : null,
        vendorName: currentUser ? currentUser.displayName : null,
        productId: isEditMode ? existingProduct.id : undefined,
      };

      const url = isEditMode ? 'https://updateproduct-fr7xfbboya-uc.a.run.app' : 'https://uploadproduct-fr7xfbboya-uc.a.run.app';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`Product ${isEditMode ? 'updated' : 'uploaded'} successfully:`, result);
        toast.success(`Product ${isEditMode ? 'updated' : 'uploaded'} successfully!`);
        navigate('/dashboard');
      } else {
        console.error(`Failed to ${isEditMode ? 'update' : 'upload'} product:`, response.statusText);
        toast.error(`Failed to ${isEditMode ? 'update' : 'upload'} product: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'uploading'} product:`, error);
      toast.error(`An error occurred: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="upload-product-container">
      <Toaster />
      <form className="upload-product-form" onSubmit={handleSubmit}>
        <h1>{isEditMode ? 'MODIFY YOUR GARMENT' : 'UPLOAD YOUR GARMENT'}</h1>
        <p>Add photos, details, pricing, and how renters can receive and return the piece.</p>

        <div className="form-section">
          <div className="image-upload-box" onClick={() => imagePreviews.length === 0 && fileInputRef.current.click()}>
            {imagePreviews.length > 0 ? (
              <div className="image-preview-grid">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="preview-image-container">
                    <img src={preview} alt={`preview ${index}`} className="preview-image" />
                    <button onClick={(e) => { e.stopPropagation(); handleRemoveImage(index); }} className="remove-image-button">X</button>
                  </div>
                ))}
                 {images.length < 12 && (
                  <div className="add-more-images" onClick={() => fileInputRef.current.click()}>
                    +
                  </div>
                )}
              </div>
            ) : (
              <p>Click to upload</p>
            )}
            <input type="file" multiple onChange={handleImageUpload} ref={fileInputRef} style={{ display: 'none' }} />
          </div>
        </div>

        <div className="form-section">
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="productName" className="bold-label">Title</label>
              <input type="text" id="productName" value={productData.productName} onChange={handleInputChange} placeholder="e.g., Structured Black Blazer Set"/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">Type</label>
              <select id="type" value={productData.type} onChange={handleInputChange}>
                <option>Jacket / Blazer</option>
                <option>Dress</option>
                <option>Bottoms</option>
                <option>Set / Suit</option>
                <option>Outerwear</option>
                <option>Top</option>
                <option>Accessories</option>

                {/* Add more options as needed */}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="condition">Condition</label>
              <select id="condition" value={productData.condition} onChange={handleInputChange}>
                <option>Excellent</option>
                <option>Good</option>
                <option>Fair</option>
                <option>Poor</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="size">Size</label>
              <select id="size" value={size} onChange={(e) => setSize(e.target.value)}>
                <option value="">Select size...</option>
                {SIZE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <select id="location" value={locationValue} onChange={(e) => setLocationValue(e.target.value)}>
                <option value="">Select location...</option>
                {LOCATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="color">Color</label>
              <select id="color" value={colorValue} onChange={(e) => setColorValue(e.target.value)}>
                <option value="">Select color...</option>
                {COLOR_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="availability">Availability</label>
              <select
                id="availability"
                value={availabilityValue}
                onChange={(e) => setAvailabilityValue(e.target.value)}
              >
                <option value="">Select availability...</option>
                {AVAILABILITY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

          <hr />
          <br/><br/>


          <div className="form-section">
              <div className="form-row">
                  <div className="form-group full-width description-group">
                      <label htmlFor="description">Description</label>
                      <textarea id="description" rows="4" value={productData.description} onChange={handleInputChange} placeholder="Describe your garment: fabric, fit, occasions, any flaws…"></textarea>
                  </div>
              </div>
          </div>

        <div className="form-actions">
          {warning && <p style={{ color: 'red' }}>{warning}</p>}
          <button type="submit" className="publish-button" disabled={isLoading}>
            {isLoading ? (isEditMode ? 'Publishing Changes...' : 'Publishing...') : (isEditMode ? 'Publish Changes' : 'Publish')}
          </button>
          <p>By publishing, you agree to provide accurate information and to handle the garment responsibly.</p>
        </div>
      </form>
      <div className="preview-section">
        <h2>Preview</h2>
        <div className="product-card-preview">
          <div className="product-card-preview-image-container">
            {imagePreviews.length > 0 ? (
              <img src={imagePreviews[0]} alt="Product Preview" className="product-card-preview-image" />
            ) : (
              <div className="product-card-preview-image-placeholder"></div>
            )}
          </div>
          <div className="product-card-preview-details">
            <div className="product-card-preview-title-price">
              <span className="product-card-preview-title">{productData.productName || 'Your title'}</span>
              {/* <span className="product-card-preview-price">{productData.price ? `€${productData.price}/day` : '€65/day'}</span> */}
          </div>
            <span className="product-card-preview-size">{size || 'IT 40'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadProduct;
